import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Workflow, Search, Mail, MessageSquare, IdCard, FileText, X, ShieldCheck, Clock, Send, Loader2, Reply, Smartphone, Pencil, Hash, Check } from "lucide-react";

// ===================== Shared helpers =====================
const STAGES = [
  { key: "lead", label: "LEAD", color: "bg-zinc-700 text-white border-zinc-600", accent: "border-zinc-500" },
  { key: "pending", label: "CLUB PENDING", color: "bg-orange-500 text-black border-orange-400", accent: "border-orange-400" },
  { key: "client", label: "CLIENTE", color: "bg-green-500 text-black border-green-400", accent: "border-green-400" },
  { key: "vip", label: "VIP FEDELTÀ", color: "bg-yellow-400 text-black border-yellow-300", accent: "border-yellow-300" },
];

const stageMeta = (key) => STAGES.find((s) => s.key === key) || STAGES[0];

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso;
  }
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

// ===================== Pipeline (kanban) =====================
export function PipelinePage() {
  const [data, setData] = useState({ stages: [], counts: {}, grouped: {} });
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [dragOver, setDragOver] = useState(null);
  const [campaignStage, setCampaignStage] = useState(null);  // open dialog when set

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/crm/pipeline");
      setData(data);
    } catch (e) {
      toast.error("Errore caricamento pipeline");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const onDragStart = (uid) => (e) => {
    setDragId(uid);
    e.dataTransfer.effectAllowed = "move";
    try { e.dataTransfer.setData("text/plain", uid); } catch { /* noop */ }
  };

  const onDragEnd = () => {
    setDragId(null);
    setDragOver(null);
  };

  const onDragOver = (stageKey) => (e) => {
    e.preventDefault();
    if (dragOver !== stageKey) setDragOver(stageKey);
    e.dataTransfer.dropEffect = "move";
  };

  const onDrop = (stageKey) => async (e) => {
    e.preventDefault();
    const uid = dragId || e.dataTransfer.getData("text/plain");
    setDragOver(null);
    setDragId(null);
    if (!uid) return;
    // Find which stage this card is currently in
    let from = null;
    for (const s of STAGES) {
      if ((data.grouped?.[s.key] || []).find((c) => c.user_id === uid)) { from = s.key; break; }
    }
    if (from === stageKey) return;
    // Optimistic UI update
    setData((d) => {
      const card = (d.grouped?.[from] || []).find((c) => c.user_id === uid);
      if (!card) return d;
      const newGrouped = { ...d.grouped };
      newGrouped[from] = (newGrouped[from] || []).filter((c) => c.user_id !== uid);
      newGrouped[stageKey] = [{ ...card, stage: stageKey, stage_source: "manual" }, ...(newGrouped[stageKey] || [])];
      const newCounts = { ...d.counts, [from]: (d.counts[from] || 1) - 1, [stageKey]: (d.counts[stageKey] || 0) + 1 };
      return { ...d, grouped: newGrouped, counts: newCounts };
    });
    try {
      await api.patch(`/admin/crm/contacts/${uid}/stage`, { stage: stageKey });
      toast.success(`Spostato in ${stageMeta(stageKey).label}`);
    } catch (err) {
      toast.error("Errore spostamento — ripristino");
      await load();
    }
  };

  return (
    <div data-testid="crm-pipeline">
      <div className="mb-8">
        <div className="font-jp text-yellow-400 text-xs tracking-widest">パイプライン</div>
        <h1 className="font-heading text-4xl uppercase text-white">Pipeline CRM</h1>
        <p className="text-zinc-400 mt-2 text-sm">
          Visualizzazione automatica per fase. <span className="text-yellow-400">Trascina una card</span> per spostarla manualmente in un&apos;altra fase.
        </p>
      </div>

      {loading ? (
        <div className="text-zinc-500 font-heading uppercase">Caricamento…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {STAGES.map((s) => {
            const items = data.grouped?.[s.key] || [];
            const highlighted = dragOver === s.key && dragId;
            return (
              <div
                key={s.key}
                data-testid={`stage-col-${s.key}`}
                onDragOver={onDragOver(s.key)}
                onDragLeave={() => dragOver === s.key && setDragOver(null)}
                onDrop={onDrop(s.key)}
                className={`bg-zinc-900 border-2 transition ${highlighted ? "border-yellow-400 shadow-[0_0_24px_rgba(250,204,21,0.35)]" : "border-zinc-800"}`}
              >
                <div className={`px-4 py-3 ${s.color} font-heading tracking-widest text-sm flex items-center justify-between gap-2`}>
                  <span className="truncate">{s.label}</span>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className="bg-black/30 px-2 py-0.5 text-xs">{items.length}</span>
                    {items.some((c) => c.phone) && (
                      <button
                        type="button"
                        data-testid={`campaign-btn-${s.key}`}
                        onClick={(e) => { e.stopPropagation(); setCampaignStage(s.key); }}
                        title="Invia campagna SMS a tutta la fase"
                        className="bg-black/40 hover:bg-black/60 p-1 transition"
                      >
                        <Smartphone className="w-3.5 h-3.5" strokeWidth={3} />
                      </button>
                    )}
                  </div>
                </div>
                <div className="p-3 space-y-2 max-h-[70vh] overflow-y-auto min-h-[200px]">
                  {items.length === 0 && (
                    <div className="text-zinc-600 text-xs uppercase tracking-widest text-center py-8">
                      {highlighted ? "Rilascia qui" : "Vuoto"}
                    </div>
                  )}
                  {items.map((c) => (
                    <div
                      key={c.user_id}
                      draggable
                      onDragStart={onDragStart(c.user_id)}
                      onDragEnd={onDragEnd}
                      data-testid={`pipeline-card-${c.user_id}`}
                      className={`group relative cursor-grab active:cursor-grabbing ${dragId === c.user_id ? "opacity-50" : ""}`}
                    >
                      <button
                        type="button"
                        onClick={() => setSelectedId(c.user_id)}
                        className={`w-full text-left bg-zinc-950 border-2 ${c.handoff_requested ? "border-red-500" : s.accent} hover:bg-zinc-900 p-3 transition relative`}
                      >
                        {c.handoff_requested && (
                          <div data-testid={`handoff-badge-${c.user_id}`} className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] font-heading uppercase tracking-widest px-1.5 py-0.5 border-2 border-black animate-pulse">
                            ● Handoff
                          </div>
                        )}
                        <div className="font-heading uppercase text-white text-sm truncate">{c.name || "—"}</div>
                        <div className="text-[11px] text-zinc-400 font-mono truncate mt-0.5">{c.email}</div>
                        {c.phone && (
                          <div className="text-[11px] text-zinc-500 font-mono truncate">📞 {c.phone}</div>
                        )}
                        <div className="flex items-center justify-between mt-2 text-[10px] uppercase tracking-widest">
                          <span className="text-zinc-500">{c.orders_count} ordini</span>
                          <span className="text-yellow-400 font-mono">€ {c.total_spent.toFixed(2)}</span>
                        </div>
                        {c.stage_source === "manual" && (
                          <div className="mt-1 text-[10px] text-yellow-400 uppercase tracking-widest">forzato manuale</div>
                        )}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selectedId && <ContactDrawer userId={selectedId} onClose={() => setSelectedId(null)} onChanged={load} />}
      {campaignStage && (
        <CampaignDialog
          stage={campaignStage}
          targets={data.grouped?.[campaignStage] || []}
          onClose={() => setCampaignStage(null)}
        />
      )}
    </div>
  );
}

function CampaignDialog({ stage, targets, onClose }) {
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [schedule, setSchedule] = useState(false);
  const [scheduledAt, setScheduledAt] = useState("");
  const sm = stageMeta(stage);
  const phoneTargets = targets.filter((c) => c.phone);

  // Default schedule = "now + 1 hour" in local datetime-local format
  const defaultScheduleValue = (() => {
    const d = new Date(Date.now() + 60 * 60 * 1000);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  })();

  const send = async () => {
    if (!text.trim()) return;
    if (schedule && !scheduledAt) { toast.error("Scegli data/ora di invio"); return; }
    const when = schedule ? scheduledAt : null;
    const msg = schedule
      ? `Programmare l'SMS per ${new Date(scheduledAt).toLocaleString("it-IT")}? Target stimati: ${phoneTargets.length}`
      : `Inviare l'SMS a ${phoneTargets.length} contatti in ${sm.label}?`;
    if (!confirm(msg)) return;
    setSending(true);
    try {
      const body = { stage, text: text.trim() };
      if (when) body.scheduled_at = new Date(when).toISOString();
      const { data } = await api.post("/admin/integrations/smshosting/campaign/sms", body);
      setResult(data);
      if (data.scheduled) {
        toast.success(`Campagna programmata per ${new Date(data.scheduled_at).toLocaleString("it-IT")}`);
        setTimeout(onClose, 1500);
      } else {
        toast.success(`Inviati ${data.sent}/${data.targets} SMS`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Operazione fallita");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[85]" data-testid="campaign-dialog">
      <div onClick={onClose} className="absolute inset-0 bg-black/85" />
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(560px,calc(100vw-2rem))] bg-zinc-950 border-2 border-yellow-400 shadow-[8px_8px_0_0_rgba(0,0,0,0.9)]">
        <header className="bg-yellow-400 text-black px-4 py-3 flex items-center justify-between">
          <div>
            <div className="font-jp text-[10px] tracking-widest">キャンペーン</div>
            <div className="font-heading uppercase text-lg">Campagna SMS — {sm.label}</div>
          </div>
          <button onClick={onClose} className="p-1.5 bg-black text-yellow-400 hover:bg-zinc-900" aria-label="Chiudi">
            <X className="w-4 h-4" strokeWidth={3} />
          </button>
        </header>
        <div className="p-5 space-y-3">
          <div className="text-zinc-300 text-sm">
            {schedule ? "Sarà inviato a " : "Stai per inviare un SMS a "}
            <span className="text-yellow-400 font-mono">{phoneTargets.length}</span> contatti
            {phoneTargets.length < targets.length && (
              <span className="text-zinc-500"> ({targets.length - phoneTargets.length} senza numero saranno saltati)</span>
            )}.
          </div>
          <textarea
            data-testid="campaign-text"
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, 160))}
            placeholder="Testo SMS (max 160 caratteri)…"
            rows={4}
            className="w-full bg-zinc-900 border-2 border-zinc-800 focus:border-yellow-400 text-white p-3 font-mono text-sm outline-none resize-none"
          />
          <div className="flex items-center justify-between text-[10px] text-zinc-500 uppercase tracking-widest">
            <span>Mittente: Daitanjack</span>
            <span className={text.length > 140 ? "text-yellow-400" : ""}>{text.length}/160</span>
          </div>

          {/* Schedule toggle */}
          <div className="border-t-2 border-zinc-800 pt-3">
            <label className="flex items-center gap-2 cursor-pointer text-sm text-zinc-300">
              <input
                type="checkbox"
                data-testid="schedule-toggle"
                checked={schedule}
                onChange={(e) => { setSchedule(e.target.checked); if (e.target.checked && !scheduledAt) setScheduledAt(defaultScheduleValue); }}
                className="accent-yellow-400 w-4 h-4"
              />
              <span className="font-heading uppercase tracking-widest text-xs">Programma invio</span>
            </label>
            {schedule && (
              <input
                type="datetime-local"
                data-testid="schedule-datetime"
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
                className="mt-2 w-full bg-zinc-900 border-2 border-yellow-400 text-white px-3 py-2 font-mono text-xs outline-none"
              />
            )}
          </div>

          {result && !result.scheduled && (
            <div className="text-xs font-mono p-2 border border-zinc-800 bg-zinc-900">
              ✓ Inviati: <span className="text-green-400">{result.sent}</span> · Falliti: <span className="text-red-400">{result.failed}</span>
              {result.errors?.length > 0 && (
                <ul className="mt-1 text-red-300 text-[10px]">
                  {result.errors.map((e, i) => <li key={i}>· {e}</li>)}
                </ul>
              )}
            </div>
          )}
          <div className="flex gap-2 pt-2">
            <button
              data-testid="campaign-send"
              onClick={send}
              disabled={sending || !text.trim() || phoneTargets.length === 0}
              className="flex-1 flex items-center justify-center gap-2 bg-yellow-400 text-black font-heading uppercase tracking-widest text-sm py-3 hover:bg-white transition disabled:opacity-40"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" strokeWidth={3} />}
              {schedule ? "Programma" : "Invia campagna"}
            </button>
            <button onClick={onClose} className="border-2 border-zinc-700 text-zinc-300 hover:border-yellow-400 hover:text-yellow-400 px-4 text-sm font-heading uppercase tracking-widest">
              Chiudi
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ===================== Contacts table =====================
export function ContactsPage() {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [stage, setStage] = useState("");
  const [selectedId, setSelectedId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (stage) params.stage = stage;
      const { data } = await api.get("/admin/crm/contacts", { params });
      setContacts(data);
    } catch (e) {
      toast.error("Errore caricamento contatti");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, stage]);

  const filteredCounts = useMemo(() => {
    const out = { lead: 0, pending: 0, client: 0, vip: 0 };
    contacts.forEach((c) => { out[c.stage] = (out[c.stage] || 0) + 1; });
    return out;
  }, [contacts]);

  return (
    <div data-testid="crm-contacts">
      <div className="mb-6">
        <div className="font-jp text-yellow-400 text-xs tracking-widest">連絡先</div>
        <h1 className="font-heading text-4xl uppercase text-white">Contatti</h1>
        <p className="text-zinc-400 mt-2 text-sm">Anagrafica completa con storico ordini e timeline conversazioni.</p>
      </div>

      <div className="bg-zinc-900 border-2 border-zinc-800 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-yellow-400" strokeWidth={3} />
            <input
              data-testid="contacts-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cerca per nome o email…"
              className="w-full bg-zinc-950 border-2 border-zinc-800 focus:border-yellow-400 text-white pl-10 pr-3 py-2.5 font-mono text-sm outline-none"
            />
          </div>
          <div className="flex gap-1.5 flex-wrap">
            <button
              data-testid="filter-all"
              onClick={() => setStage("")}
              className={`px-3 py-2 font-heading uppercase text-xs tracking-widest border-2 ${stage === "" ? "bg-yellow-400 text-black border-yellow-400" : "bg-transparent text-zinc-400 border-zinc-700 hover:border-yellow-400"}`}
            >
              Tutti ({contacts.length})
            </button>
            {STAGES.map((s) => (
              <button
                key={s.key}
                data-testid={`filter-${s.key}`}
                onClick={() => setStage(s.key === stage ? "" : s.key)}
                className={`px-3 py-2 font-heading uppercase text-xs tracking-widest border-2 ${stage === s.key ? s.color : "bg-transparent text-zinc-400 border-zinc-700 hover:border-yellow-400"}`}
              >
                {s.label} ({filteredCounts[s.key] || 0})
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-zinc-900 border-2 border-zinc-800 overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-3 bg-zinc-950 text-zinc-500 text-xs font-heading uppercase tracking-widest border-b-2 border-zinc-800">
          <div className="col-span-3">Nome</div>
          <div className="col-span-3">Email</div>
          <div className="col-span-1 text-center">Stage</div>
          <div className="col-span-1 text-center">Ordini</div>
          <div className="col-span-2 text-right">Spesa</div>
          <div className="col-span-2 text-right">Iscritto</div>
        </div>
        {loading ? (
          <div className="p-8 text-zinc-500 uppercase font-heading text-center">Caricamento…</div>
        ) : contacts.length === 0 ? (
          <div className="p-8 text-zinc-500 uppercase font-heading text-center">Nessun contatto</div>
        ) : (
          contacts.map((c) => {
            const sm = stageMeta(c.stage);
            return (
              <button
                key={c.user_id}
                data-testid={`contact-row-${c.user_id}`}
                onClick={() => setSelectedId(c.user_id)}
                className={`w-full grid grid-cols-12 px-4 py-3 text-left items-center border-b border-zinc-800 hover:bg-zinc-950 transition ${c.handoff_requested ? "bg-red-950/20" : ""}`}
              >
                <div className="col-span-3 text-white font-heading uppercase truncate flex items-center gap-2">
                  {c.handoff_requested && (
                    <span data-testid={`handoff-badge-${c.user_id}`} className="bg-red-500 text-white text-[9px] font-heading uppercase tracking-widest px-1.5 py-0.5 border border-red-400 animate-pulse">●</span>
                  )}
                  {c.name || "—"}
                </div>
                <div className="col-span-3 text-zinc-400 font-mono text-xs truncate">{c.email}</div>
                <div className="col-span-1 flex justify-center">
                  <span className={`px-2 py-0.5 text-[10px] font-heading uppercase tracking-widest border ${sm.color}`}>{sm.label}</span>
                </div>
                <div className="col-span-1 text-center text-white font-mono">{c.orders_count}</div>
                <div className="col-span-2 text-right text-yellow-400 font-mono">€ {c.total_spent.toFixed(2)}</div>
                <div className="col-span-2 text-right text-zinc-500 text-xs">{fmtDate(c.created_at)}</div>
              </button>
            );
          })
        )}
      </div>

      {selectedId && <ContactDrawer userId={selectedId} onClose={() => setSelectedId(null)} onChanged={load} />}
    </div>
  );
}

// ===================== Contact Drawer (used by both pages) =====================
function ContactDrawer({ userId, onClose, onChanged }) {
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noteText, setNoteText] = useState("");
  const [adding, setAdding] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailSubject, setEmailSubject] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);
  const [smsOpen, setSmsOpen] = useState(false);
  const [smsText, setSmsText] = useState("");
  const [sendingSms, setSendingSms] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/crm/contacts/${userId}`);
      setC(data);
    } catch (e) {
      toast.error("Errore caricamento contatto");
      onClose();
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  const setStage = async (newStage) => {
    try {
      await api.patch(`/admin/crm/contacts/${userId}/stage`, { stage: newStage });
      toast.success(newStage === "" ? "Stage auto ripristinato" : "Stage aggiornato");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error("Aggiornamento stage fallito");
    }
  };

  const resolveHandoff = async () => {
    try {
      await api.post(`/admin/crm/contacts/${userId}/handoff/resolve`);
      toast.success("Handoff segnato come gestito");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error("Operazione fallita");
    }
  };

  const openEdit = () => {
    if (!c) return;
    setEditForm({
      name: c.name || "",
      email: c.email || "",
      phone: c.phone || "",
      shipping_address: c.shipping_address || "",
      city: c.city || "",
      postal_code: c.postal_code || "",
      country: c.country || "",
    });
    setEditMode(true);
  };

  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      await api.patch(`/admin/crm/contacts/${userId}/profile`, editForm);
      toast.success("Anagrafica aggiornata");
      setEditMode(false);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Aggiornamento fallito");
    } finally {
      setSavingEdit(false);
    }
  };

  const sendEmail = async () => {
    if (!emailSubject.trim() || !emailBody.trim()) {
      toast.error("Oggetto e messaggio obbligatori");
      return;
    }
    setSendingEmail(true);
    try {
      await api.post(`/admin/crm/contacts/${userId}/email`, {
        subject: emailSubject.trim(),
        body: emailBody.trim(),
      });
      toast.success("Email inviata");
      setEmailSubject("");
      setEmailBody("");
      setEmailOpen(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invio fallito");
    } finally {
      setSendingEmail(false);
    }
  };

  const sendSms = async () => {
    if (!smsText.trim()) { toast.error("Testo SMS obbligatorio"); return; }
    if (smsText.length > 160) { toast.error("SMS max 160 caratteri"); return; }
    setSendingSms(true);
    try {
      await api.post(`/admin/integrations/smshosting/contacts/${userId}/sms`, { text: smsText.trim() });
      toast.success("SMS inviato via Smshosting");
      setSmsText("");
      setSmsOpen(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invio SMS fallito");
    } finally {
      setSendingSms(false);
    }
  };

  const applyTemplate = (key) => {
    const name = (c?.name || "").split(" ")[0] || "";
    const templates = {
      reply_handoff: {
        subject: "Ti ho risposto — Daita 'n' Jack",
        body: `Ciao ${name},\n\ngrazie per averci scritto in chat. Ti ricontatto come richiesto.\n\nDimmi pure come posso aiutarti, sono qui.\n\nA presto,\nSilvano / Filippo`,
      },
      shipping_update: {
        subject: "Aggiornamento sul tuo ordine",
        body: `Ciao ${name},\n\nti aggiorno sullo stato del tuo ordine:\n\n[inserisci dettagli]\n\nPer qualsiasi cosa, rispondi a questa mail.\n\nA presto,\nDaita 'n' Jack`,
      },
      club_welcome: {
        subject: "Benvenuto nel Daita 'n' Club",
        body: `Ciao ${name},\n\nbenvenuto ufficialmente nel Daita 'n' Club! ⚡\n\nDa ora puoi accedere a tutti i drop in anteprima e completare gli acquisti dal sito.\n\nBuona caccia!\nDaita 'n' Jack`,
      },
    };
    const tpl = templates[key];
    if (tpl) {
      setEmailSubject(tpl.subject);
      setEmailBody(tpl.body);
    }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    setAdding(true);
    try {
      await api.post(`/admin/crm/contacts/${userId}/notes`, { body: noteText.trim() });
      setNoteText("");
      toast.success("Nota aggiunta");
      await load();
    } catch (e) {
      toast.error("Aggiunta nota fallita");
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80]" data-testid="contact-drawer">
      <div onClick={onClose} className="absolute inset-0 bg-black/80" />
      <aside className="absolute right-0 top-0 bottom-0 w-full max-w-2xl bg-zinc-950 border-l-2 border-yellow-400 overflow-y-auto">
        <header className="sticky top-0 bg-zinc-950 border-b-2 border-zinc-800 p-4 flex items-center justify-between z-10">
          <div>
            <div className="font-jp text-yellow-400 text-[10px] tracking-widest">プロフィール</div>
            <div className="font-heading text-xl uppercase text-white">Scheda contatto</div>
          </div>
          <button data-testid="drawer-close" onClick={onClose} className="p-2 border-2 border-zinc-800 hover:border-yellow-400 hover:text-yellow-400 text-white">
            <X className="w-4 h-4" strokeWidth={3} />
          </button>
        </header>

        {loading || !c ? (
          <div className="p-8 text-zinc-500 uppercase font-heading">Caricamento…</div>
        ) : (
          <div className="p-5 space-y-6">
            {/* Header anagrafica */}
            <div>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  {!editMode ? (
                    <>
                      <h2 className="font-heading text-3xl uppercase text-white">{c.name || "—"}</h2>
                      <div className="text-zinc-400 font-mono text-sm">{c.email}</div>
                      {c.phone && (
                        <div className="text-zinc-400 font-mono text-sm flex items-center gap-1.5 mt-0.5">
                          <span>📞</span>{c.phone}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="space-y-2" data-testid="edit-form">
                      <input value={editForm.name} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} placeholder="Nome Cognome" className="w-full bg-zinc-900 border-2 border-yellow-400 text-white px-3 py-2 font-mono text-sm" />
                      <input value={editForm.email} onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))} placeholder="Email" className="w-full bg-zinc-900 border-2 border-zinc-800 focus:border-yellow-400 text-white px-3 py-2 font-mono text-xs" />
                      <input value={editForm.phone} onChange={(e) => setEditForm((f) => ({ ...f, phone: e.target.value }))} placeholder="Telefono" className="w-full bg-zinc-900 border-2 border-zinc-800 focus:border-yellow-400 text-white px-3 py-2 font-mono text-xs" />
                      <input value={editForm.shipping_address} onChange={(e) => setEditForm((f) => ({ ...f, shipping_address: e.target.value }))} placeholder="Indirizzo" className="w-full bg-zinc-900 border-2 border-zinc-800 focus:border-yellow-400 text-white px-3 py-2 font-mono text-xs" />
                      <div className="grid grid-cols-3 gap-2">
                        <input value={editForm.postal_code} onChange={(e) => setEditForm((f) => ({ ...f, postal_code: e.target.value }))} placeholder="CAP" className="bg-zinc-900 border-2 border-zinc-800 focus:border-yellow-400 text-white px-2 py-2 font-mono text-xs" />
                        <input value={editForm.city} onChange={(e) => setEditForm((f) => ({ ...f, city: e.target.value }))} placeholder="Città" className="bg-zinc-900 border-2 border-zinc-800 focus:border-yellow-400 text-white px-2 py-2 font-mono text-xs" />
                        <input value={editForm.country} onChange={(e) => setEditForm((f) => ({ ...f, country: e.target.value }))} placeholder="Paese" className="bg-zinc-900 border-2 border-zinc-800 focus:border-yellow-400 text-white px-2 py-2 font-mono text-xs" />
                      </div>
                      <div className="flex gap-2 pt-1">
                        <button data-testid="save-edit" onClick={saveEdit} disabled={savingEdit} className="flex items-center gap-1.5 bg-yellow-400 text-black px-3 py-2 font-heading uppercase tracking-widest text-xs hover:bg-white disabled:opacity-40">
                          {savingEdit ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" strokeWidth={3} />}
                          Salva
                        </button>
                        <button onClick={() => setEditMode(false)} className="border-2 border-zinc-700 text-zinc-400 hover:text-yellow-400 px-3 py-2 text-xs font-heading uppercase tracking-widest">Annulla</button>
                      </div>
                    </div>
                  )}
                </div>
                {!editMode && (
                  <button data-testid="edit-contact" onClick={openEdit} className="bg-zinc-900 border-2 border-zinc-800 hover:border-yellow-400 hover:text-yellow-400 text-zinc-300 p-2 transition shrink-0" aria-label="Modifica">
                    <Pencil className="w-4 h-4" strokeWidth={3} />
                  </button>
                )}
              </div>
              {/* Unique registration code + timestamp */}
              <div className="mt-3 flex items-center gap-3 text-[11px] font-mono text-zinc-500 flex-wrap">
                <span className="inline-flex items-center gap-1 bg-zinc-900 border border-zinc-800 px-2 py-1">
                  <Hash className="w-3 h-3 text-yellow-400" />
                  <span data-testid="contact-uid">{c.user_id}</span>
                </span>
                <span className="text-zinc-600">·</span>
                <span>Reg: {fmtDateTime(c.created_at)}</span>
                {c.source && <span className="text-zinc-600">· src: {c.source}</span>}
              </div>
            </div>

            {/* Handoff banner */}
            {c.handoff_requested && (
              <div data-testid="handoff-banner" className="bg-red-500/10 border-2 border-red-500 p-4 flex items-center justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 text-red-400 font-heading uppercase tracking-widest text-sm">
                    <span className="inline-block w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse" />
                    Richiesta intervento umano
                  </div>
                  <div className="text-zinc-300 text-xs mt-1">
                    Inviata: {fmtDateTime(c.handoff_at)}
                  </div>
                </div>
                <button
                  data-testid="resolve-handoff-btn"
                  onClick={resolveHandoff}
                  className="bg-red-500 text-white font-heading uppercase tracking-widest text-xs px-3 py-2 hover:bg-red-400 transition"
                >
                  Segna come gestito
                </button>
              </div>
            )}

            {/* Stage card */}
            <div className="bg-zinc-900 border-2 border-zinc-800 p-4">
              <div className="text-xs text-zinc-500 uppercase tracking-widest mb-2 flex items-center justify-between">
                <span>Fase pipeline</span>
                <span className="text-zinc-600">{c.stage_source === "manual" ? "manuale" : "auto"}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {STAGES.map((s) => (
                  <button
                    key={s.key}
                    data-testid={`set-stage-${s.key}`}
                    onClick={() => setStage(s.key)}
                    className={`px-3 py-1.5 text-xs font-heading uppercase tracking-widest border-2 transition ${c.stage === s.key ? s.color : "bg-transparent text-zinc-400 border-zinc-700 hover:border-yellow-400"}`}
                  >
                    {s.label}
                  </button>
                ))}
                {c.stage_source === "manual" && (
                  <button
                    data-testid="reset-stage-auto"
                    onClick={() => setStage("")}
                    className="px-3 py-1.5 text-xs font-heading uppercase tracking-widest border-2 border-yellow-400 text-yellow-400 hover:bg-yellow-400 hover:text-black"
                  >
                    ↺ Auto
                  </button>
                )}
              </div>
            </div>

            {/* Quick stats */}
            <div className="grid grid-cols-3 gap-3">
              <Stat label="Ordini" value={c.orders_count} />
              <Stat label="Spesa" value={`€ ${c.total_spent.toFixed(2)}`} />
              <Stat label="Card" value={c.has_card ? "SI" : "—"} />
            </div>

            {/* Indirizzo */}
            {(c.shipping_address || c.city) && (
              <Section icon={ShieldCheck} title="Indirizzo">
                <div className="text-zinc-300 text-sm leading-relaxed">
                  {c.shipping_address}<br />
                  {c.postal_code} {c.city}{c.country ? `, ${c.country}` : ""}
                </div>
              </Section>
            )}

            {/* Card request */}
            {c.card_request && (
              <Section icon={IdCard} title="Richiesta Card Club">
                <div className="text-zinc-300 text-sm">
                  Stato: <span className="text-yellow-400 uppercase font-heading">{c.card_request.status || "pending"}</span><br />
                  Richiesta: {fmtDate(c.card_request.created_at)}
                </div>
              </Section>
            )}

            {/* Orders */}
            <Section icon={Clock} title={`Ordini (${c.orders.length})`}>
              {c.orders.length === 0 ? (
                <div className="text-zinc-500 text-sm uppercase tracking-widest">Nessun ordine</div>
              ) : (
                <div className="space-y-2">
                  {c.orders.map((o) => (
                    <div key={o.id} data-testid={`order-${o.id}`} className="bg-zinc-900 border border-zinc-800 p-3">
                      <div className="flex items-center justify-between">
                        <div className="font-mono text-yellow-400 text-sm">#{o.id.slice(0, 8).toUpperCase()}</div>
                        <div className="text-zinc-500 text-xs">{fmtDateTime(o.created_at)}</div>
                      </div>
                      <div className="flex items-center justify-between mt-1.5">
                        <span className="text-zinc-400 text-xs uppercase tracking-widest">{(o.payment_status || "—")} · {o.status}</span>
                        <span className="font-mono text-white">€ {Number(o.total || 0).toFixed(2)}</span>
                      </div>
                      <div className="mt-2 text-xs text-zinc-500">
                        {(o.items || []).map((it) => `${it.quantity}× ${it.title}`).join(", ")}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Section>

            {/* SMS via Smshosting */}
            {c.phone && (
              <Section icon={Smartphone} title="Invia SMS (Smshosting)">
                {!smsOpen ? (
                  <button
                    data-testid="open-sms"
                    onClick={() => setSmsOpen(true)}
                    className="flex items-center gap-2 bg-yellow-400 text-black font-heading uppercase tracking-widest text-xs px-3 py-2 hover:bg-white transition"
                  >
                    <Smartphone className="w-4 h-4" strokeWidth={3} /> Scrivi SMS
                  </button>
                ) : (
                  <div className="space-y-2 bg-zinc-900 border-2 border-yellow-400 p-3">
                    <textarea
                      data-testid="sms-body"
                      value={smsText}
                      onChange={(e) => setSmsText(e.target.value.slice(0, 160))}
                      placeholder="Testo SMS… (max 160 caratteri)"
                      rows={3}
                      className="w-full bg-zinc-950 border-2 border-zinc-800 focus:border-yellow-400 text-white p-3 font-mono text-xs outline-none resize-none"
                    />
                    <div className="flex items-center justify-between text-[10px] text-zinc-500 uppercase tracking-widest">
                      <span>Destinatario: {c.phone}</span>
                      <span className={smsText.length > 140 ? "text-yellow-400" : ""}>{smsText.length}/160</span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        data-testid="send-sms"
                        onClick={sendSms}
                        disabled={sendingSms || !smsText.trim()}
                        className="flex items-center gap-2 bg-yellow-400 text-black font-heading uppercase tracking-widest text-xs px-4 py-2 hover:bg-white transition disabled:opacity-40"
                      >
                        {sendingSms ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" strokeWidth={3} />}
                        Invia SMS
                      </button>
                      <button onClick={() => { setSmsOpen(false); setSmsText(""); }} className="border-2 border-zinc-700 text-zinc-400 hover:text-yellow-400 px-3 py-2 text-xs font-heading uppercase tracking-widest">
                        Annulla
                      </button>
                    </div>
                  </div>
                )}
              </Section>
            )}

            {/* Email reply */}
            <Section icon={Reply} title="Rispondi via email">
              {!emailOpen ? (
                <div className="flex flex-wrap gap-2">
                  <button
                    data-testid="open-email-reply"
                    onClick={() => setEmailOpen(true)}
                    className="flex items-center gap-2 bg-yellow-400 text-black font-heading uppercase tracking-widest text-xs px-3 py-2 hover:bg-white transition"
                  >
                    <Mail className="w-4 h-4" strokeWidth={3} /> Scrivi email
                  </button>
                  {c.handoff_requested && (
                    <button
                      data-testid="tpl-reply-handoff"
                      onClick={() => { setEmailOpen(true); applyTemplate("reply_handoff"); }}
                      className="border-2 border-zinc-700 text-zinc-300 hover:border-yellow-400 hover:text-yellow-400 px-3 py-2 text-xs font-heading uppercase tracking-widest"
                    >
                      Template: Risposta Handoff
                    </button>
                  )}
                  <button
                    data-testid="tpl-shipping"
                    onClick={() => { setEmailOpen(true); applyTemplate("shipping_update"); }}
                    className="border-2 border-zinc-700 text-zinc-300 hover:border-yellow-400 hover:text-yellow-400 px-3 py-2 text-xs font-heading uppercase tracking-widest"
                  >
                    Template: Ordine
                  </button>
                  <button
                    data-testid="tpl-club"
                    onClick={() => { setEmailOpen(true); applyTemplate("club_welcome"); }}
                    className="border-2 border-zinc-700 text-zinc-300 hover:border-yellow-400 hover:text-yellow-400 px-3 py-2 text-xs font-heading uppercase tracking-widest"
                  >
                    Template: Benvenuto Club
                  </button>
                </div>
              ) : (
                <div className="space-y-2 bg-zinc-900 border-2 border-yellow-400 p-3">
                  <input
                    data-testid="email-subject"
                    type="text"
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    placeholder="Oggetto"
                    className="w-full bg-zinc-950 border-2 border-zinc-800 focus:border-yellow-400 text-white px-3 py-2 font-mono text-xs outline-none"
                  />
                  <textarea
                    data-testid="email-body"
                    value={emailBody}
                    onChange={(e) => setEmailBody(e.target.value)}
                    placeholder="Scrivi il messaggio… (markdown-light: vai a capo = nuovo paragrafo)"
                    rows={8}
                    className="w-full bg-zinc-950 border-2 border-zinc-800 focus:border-yellow-400 text-white p-3 font-mono text-xs outline-none resize-none"
                  />
                  <div className="text-[10px] text-zinc-500 uppercase tracking-widest">
                    Verrà inviata da Resend con header brand a {c.email}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <button
                      data-testid="send-email"
                      onClick={sendEmail}
                      disabled={sendingEmail || !emailSubject.trim() || !emailBody.trim()}
                      className="flex items-center gap-2 bg-yellow-400 text-black font-heading uppercase tracking-widest text-xs px-4 py-2 hover:bg-white transition disabled:opacity-40"
                    >
                      {sendingEmail ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" strokeWidth={3} />}
                      Invia email
                    </button>
                    <button
                      onClick={() => { setEmailOpen(false); setEmailSubject(""); setEmailBody(""); }}
                      className="border-2 border-zinc-700 text-zinc-400 hover:text-yellow-400 px-3 py-2 text-xs font-heading uppercase tracking-widest"
                    >
                      Annulla
                    </button>
                    <div className="flex-1" />
                    <div className="flex gap-1.5 flex-wrap items-center">
                      <span className="text-[10px] text-zinc-500 uppercase tracking-widest mr-1">Template:</span>
                      <button onClick={() => applyTemplate("reply_handoff")} className="text-[10px] text-zinc-400 hover:text-yellow-400 underline">Handoff</button>
                      <button onClick={() => applyTemplate("shipping_update")} className="text-[10px] text-zinc-400 hover:text-yellow-400 underline">Ordine</button>
                      <button onClick={() => applyTemplate("club_welcome")} className="text-[10px] text-zinc-400 hover:text-yellow-400 underline">Club</button>
                    </div>
                  </div>
                </div>
              )}
            </Section>

            {/* Note input */}
            <Section icon={FileText} title="Aggiungi nota interna">
              <div className="space-y-2">
                <textarea
                  data-testid="note-input"
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder="Es. Chiamato il cliente — preferisce ritiro in negozio venerdì."
                  rows={2}
                  className="w-full bg-zinc-900 border-2 border-zinc-800 focus:border-yellow-400 text-white p-3 font-mono text-sm outline-none resize-none"
                />
                <button
                  data-testid="add-note-btn"
                  onClick={addNote}
                  disabled={adding || !noteText.trim()}
                  className="flex items-center gap-2 bg-yellow-400 text-black font-heading uppercase tracking-widest text-xs px-4 py-2 hover:bg-white transition disabled:opacity-40"
                >
                  {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" strokeWidth={3} />}
                  Salva nota
                </button>
              </div>
            </Section>

            {/* Conversations timeline */}
            <Section icon={MessageSquare} title={`Timeline conversazioni (${c.conversations.length})`}>
              {c.conversations.length === 0 ? (
                <div className="text-zinc-500 text-sm uppercase tracking-widest">Nessuna conversazione registrata</div>
              ) : (
                <ol className="space-y-3">
                  {c.conversations.map((conv) => (
                    <li key={conv.id} data-testid={`convo-${conv.id}`} className="bg-zinc-900 border border-zinc-800 p-3">
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="flex items-center gap-2">
                          <ChannelBadge channel={conv.channel} direction={conv.direction} />
                          {conv.subject && <span className="text-zinc-300 text-sm font-heading uppercase truncate">{conv.subject}</span>}
                        </div>
                        <span className="text-zinc-500 text-[11px]">{fmtDateTime(conv.created_at)}</span>
                      </div>
                      {conv.channel === "email" ? (
                        <div className="text-zinc-500 text-xs font-mono">
                          Resend ID: {conv.meta?.resend_id || "n/a"} — {conv.meta?.ok ? "✓ inviata" : "✗ fallita"}
                        </div>
                      ) : (
                        <div className="text-zinc-300 text-sm whitespace-pre-wrap break-words">{conv.body}</div>
                      )}
                    </li>
                  ))}
                </ol>
              )}
            </Section>
          </div>
        )}
      </aside>
    </div>
  );
}

// ===================== Sub-components =====================
function Stat({ label, value }) {
  return (
    <div className="bg-zinc-900 border-2 border-zinc-800 px-3 py-2.5">
      <div className="text-[10px] text-zinc-500 uppercase tracking-widest">{label}</div>
      <div className="font-heading text-xl text-yellow-400">{value}</div>
    </div>
  );
}

function Section({ icon: Icon, title, children }) {
  return (
    <section>
      <div className="flex items-center gap-2 text-xs font-heading uppercase tracking-widest text-zinc-400 mb-2">
        <Icon className="w-3.5 h-3.5 text-yellow-400" strokeWidth={3} />
        {title}
      </div>
      {children}
    </section>
  );
}

function ChannelBadge({ channel, direction }) {
  const icons = { email: Mail, sms: MessageSquare, whatsapp: MessageSquare, chat: MessageSquare, note: FileText };
  const Icon = icons[channel] || MessageSquare;
  const colors = {
    email: "bg-blue-500/20 text-blue-300 border-blue-500/40",
    note: "bg-yellow-400/20 text-yellow-300 border-yellow-400/40",
    chat: "bg-green-500/20 text-green-300 border-green-500/40",
    sms: "bg-zinc-500/20 text-zinc-300 border-zinc-500/40",
    whatsapp: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-heading uppercase tracking-widest border ${colors[channel] || colors.note}`}>
      <Icon className="w-3 h-3" strokeWidth={3} />
      {channel} · {direction === "out" ? "↗" : "↙"}
    </span>
  );
}
